import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designType } from "services/constants/designTokens";
import { FilterSelect, StatutoryPage, StatutoryTabs } from "pages/PayRoll/Shared/statutoryUi";
import {
  CHECK_DATE,
  CODES,
  DEFAULT_MONTH,
  PAYROLL_MONTHS,
  buildIssues,
  codeName,
  codeRows,
  monthRows,
  sampleChallans,
  sampleEsicFiles,
  withAmounts,
} from "./esiReportsData";
import { EsiOverviewTab } from "./EsiOverviewTab";
import { EsiRegisterTab } from "./EsiRegisterTab";
import { EsiFileTab } from "./EsiFileTab";
import { EsiReconciliationTab } from "./EsiReconciliationTab";
import { EsiValidationTab } from "./EsiValidationTab";

// Payroll > Reports > Statutory Reports > ESI (screens R1–R5a). Built from
// the finalised payroll: Cauver prepares the ESIC file, the company uploads
// it and pays on the ESIC portal. Sample data until the ESI APIs exist;
// generated files and challans stay in memory for this visit.

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "register", label: "Contribution Register" },
  { value: "esic", label: "ESIC File" },
  { value: "reconciliation", label: "Reconciliation" },
  { value: "validation", label: "Validation" },
];
const ALL = "all";
const now = () => moment().format("YYYY-MM-DDTHH:mm:ss");

export const EsiStatutoryReports = ({ onBack, initialTab }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh sample data per company.
  return <EsiReportsScreen key={`${domain}:${topbarCompanyId}`} onBack={onBack} initialTab={initialTab} />;
};

const EsiReportsScreen = ({ onBack, initialTab }) => {
  const [tab, setTab] = useState(() => (TABS.some((item) => item.value === initialTab) ? initialTab : "overview"));
  const [month, setMonth] = useState(DEFAULT_MONTH);
  const [codeFilter, setCodeFilter] = useState(ALL);
  const [department, setDepartment] = useState(ALL);
  const [files, setFiles] = useState(sampleEsicFiles);
  const [challanList, setChallanList] = useState(sampleChallans);
  const [lastCheck, setLastCheck] = useState({ at: `${CHECK_DATE}T10:20:00`, trigger: "IP number added for Divya R (auto)" });

  const monthInfo = PAYROLL_MONTHS.find((item) => item.value === month);
  const allRows = monthRows(month);
  const departments = useMemo(() => [...new Set(allRows.map((row) => row.department))].sort(), [allRows]);
  const inScope = (row) =>
    (codeFilter === ALL || row.code?.code === codeFilter) && (department === ALL || row.department === department);
  const rows = allRows.filter(inScope);
  const codes = CODES.filter((code) => codeFilter === ALL || code.code === codeFilter);
  // Files are blocked by every issue of their code, whatever the department filter.
  const allIssues = useMemo(() => buildIssues(month), [month, lastCheck]); // eslint-disable-line react-hooks/exhaustive-deps
  const issues = useMemo(
    () =>
      allIssues.filter(
        (item) => (codeFilter === ALL || item.code?.code === codeFilter) && (department === ALL || !item.department || item.department === department),
      ),
    [allIssues, codeFilter, department],
  );
  const challans = withAmounts(challanList);

  const generate = (code, fileRows, reason) => {
    const existing = files.filter((file) => file.code === code && file.month === month);
    const version = Math.max(0, ...existing.map((file) => file.version)) + 1;
    setFiles((list) => [
      { id: Math.max(0, ...list.map((file) => file.id)) + 1, code, month, version, rows: fileRows, generated_at: now(), generated_by: "You", status: "Current", reason },
      ...list.map((file) => (file.code === code && file.month === month ? { ...file, status: "Superseded" } : file)),
    ]);
    setLastCheck({ at: now(), trigger: "Generate File" });
    return version;
  };

  const record = (challan) =>
    setChallanList((list) => [
      ...list,
      { ...challan, id: Math.max(0, ...list.map((item) => item.id)) + 1, amount_delta: 0, recorded_by: "You", recorded_at: now(), history: [] },
    ]);

  const edit = (id, form) =>
    setChallanList((list) =>
      withAmounts(list).map((challan) => {
        if (challan.id !== id) return challan;
        const changes = [
          ["Amount", challan.amount, form.amount],
          ["Paid on", challan.paid_on, form.paid_on],
          ["Reference", challan.reference, form.reference],
          ["Receipt", challan.receipt, form.receipt],
          ["Remarks", challan.remarks || "—", form.remarks || "—"],
        ]
          .filter(([, from, to]) => String(from) !== String(to))
          .map(([label, from, to]) => ({ label, from: String(from), to: String(to) }));
        return {
          ...challan,
          amount: form.amount,
          paid_on: form.paid_on,
          reference: form.reference,
          receipt: form.receipt,
          remarks: form.remarks,
          history: [{ id: `${id}-${now()}`, title: `${challan.reference} edited`, at: now(), actor: { name: "You" }, changes, note: form.reason }, ...(challan.history || [])],
        };
      }),
    );

  const shared = { month, monthInfo, codeFilter, rows, codes, issues, allIssues, files, challans, goTo: setTab };

  const aside = monthInfo.finalized ? (
    <Mui.Box sx={{ textAlign: { md: "right" } }}>
      <Mui.Stack direction="row" spacing={1} alignItems="center" justifyContent={{ md: "flex-end" }}>
        <Mui.Typography sx={designType.helperText}>Payroll status</Mui.Typography>
        <RequestStatusChip status="Finalized" colorCode="#12B76A" />
      </Mui.Stack>
      <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>
        Finalized {moment(monthInfo.finalizedAt).format("DD MMM YYYY, HH:mm")} · Version {monthInfo.version}
      </Mui.Typography>
    </Mui.Box>
  ) : (
    <Mui.Stack direction="row" spacing={1} alignItems="center">
      <Mui.Typography sx={designType.helperText}>Payroll status</Mui.Typography>
      <RequestStatusChip status="Not finalized" colorCode="#6B7280" />
    </Mui.Stack>
  );

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Reports", "Statutory Reports", "ESI"]}
      title="ESI Reports"
      subtitle="Built from finalised payroll. Cauver prepares the file — your organisation uploads it and pays on the ESIC portal."
      aside={aside}
      onBack={onBack}
    >
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <FilterSelect label="Payroll Month" value={month} onChange={setMonth} options={PAYROLL_MONTHS.map((item) => ({ value: item.value, label: item.label }))} />
        <FilterSelect
          label="ESI Code"
          value={codeFilter}
          onChange={setCodeFilter}
          options={[{ value: ALL, label: "All codes" }, ...CODES.map((code) => ({ value: code.code, label: `${codeName(code)} · ${code.code}` }))]}
        />
        <FilterSelect
          label="Department"
          value={department}
          onChange={setDepartment}
          options={[{ value: ALL, label: "All departments" }, ...departments.map((item) => ({ value: item, label: item }))]}
        />
      </Mui.Stack>

      <StatutoryTabs value={tab} onChange={setTab} tabs={TABS} />

      {!monthInfo.finalized && tab !== "validation" ? (
        <RequestInfoBanner
          tone="warning"
          title="Payroll not finalised"
          body={`${monthInfo.label} payroll is not finalised yet. ESI reports open only for finalised payroll; Validation is available as a preview so issues can be fixed first.`}
          action={<SecondaryButton onClick={() => setTab("validation")} sx={{ whiteSpace: "nowrap" }}>Open Validation</SecondaryButton>}
        />
      ) : (
        <>
          {tab === "overview" ? <EsiOverviewTab {...shared} codeRowsFor={(code) => codeRows(month, code).filter((row) => department === ALL || row.department === department)} /> : null}
          {tab === "register" ? <EsiRegisterTab {...shared} /> : null}
          {tab === "esic" ? <EsiFileTab {...shared} issues={allIssues} onGenerate={generate} /> : null}
          {tab === "reconciliation" ? <EsiReconciliationTab {...shared} onRecord={record} onEdit={edit} /> : null}
          {tab === "validation" ? (
            <EsiValidationTab issues={issues} lastCheck={lastCheck} preview={!monthInfo.finalized} onRecheck={() => setLastCheck({ at: now(), trigger: "Re-check now" })} />
          ) : null}
        </>
      )}
    </StatutoryPage>
  );
};
